/**
 * useAuthManager - React Hook for AuthManager Singleton
 * 
 * Provides React integration for the centralized authentication state
 * Replaces direct Firebase Auth listeners to prevent race conditions
 */

import { useState, useEffect, useRef } from 'react';
import AuthManager, { AuthState } from './AuthManager';

export interface UseAuthManagerResult extends AuthState {
  // Helper methods
  isAuthenticated: boolean;
  isReady: boolean;
  userId: string | null;
  userEmail: string | null;
}

export function useAuthManager(): UseAuthManagerResult {
  const [authState, setAuthState] = useState<AuthState>(() => 
    AuthManager.getInstance().getState()
  );
  
  const listenerIdRef = useRef<string>();
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const authManager = AuthManager.getInstance();
    
    // Generate unique listener ID for this hook instance
    listenerIdRef.current = `useAuthManager-${Math.random().toString(36).substring(7)}`;
    
    // Initialize AuthManager if needed
    authManager.initialize().catch((error) => {
      console.error('[useAuthManager] Failed to initialize AuthManager:', error);
    });
    
    // Subscribe to auth state changes
    cleanupRef.current = authManager.addListener(
      listenerIdRef.current,
      (newState) => {
        setAuthState(newState);
      }
    );
    
    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  return {
    ...authState,
    isAuthenticated: authState.user !== null && authState.isInitialized,
    isReady: authState.isInitialized && !authState.isLoading,
    userId: authState.user?.uid || null,
    userEmail: authState.user?.email || null
  };
}